import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Search, User, Check, X, RefreshCw } from 'lucide-react';
import apiService from '../../services/api';

const UserManagement = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    fullName: '',
    email: '',
    password: '',
    departmentId: '',
    departmentIds: [],
    isActive: true,
    isAdmin: false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, deptsRes] = await Promise.all([
        apiService.request('/admin/users'),
        apiService.request('/admin/departments')
      ]);
      setUsers(usersRes);
      setDepartments(deptsRes);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      fullName: '',
      email: '',
      password: '',
      departmentId: departments[0]?.id || '',
      departmentIds: departments[0]?.id ? [departments[0].id] : [],
      isActive: true,
      isAdmin: false
    });
    setError('');
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    const deptIds = user.departmentIds?.length > 0 ? user.departmentIds : [user.departmentId];
    setFormData({
      username: user.username,
      fullName: user.fullName,
      email: user.email || '',
      password: '',
      departmentId: user.departmentId,
      departmentIds: deptIds,
      isActive: user.isActive,
      isAdmin: user.isAdmin,
      canDeletePatientMessages: user.canDeletePatientMessages || false
    });
    setError('');
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(t('admin.users.confirmDelete', { name: user.fullName }))) return;

    try {
      await apiService.request(`/admin/users/${user.id}`, { method: 'DELETE' });
      loadData();
    } catch (err) {
      alert(t('admin.users.deleteFailed') + ': ' + (err.message || t('admin.common.error')));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (editingUser) {
        await apiService.request(`/admin/users/${editingUser.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
      } else {
        await apiService.request('/admin/users', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.message || t('admin.common.error'));
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <RefreshCw size={24} color="#7c3aed" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          background: 'white',
          borderRadius: '8px',
          padding: '8px 12px',
          flex: 1,
          maxWidth: '300px',
          border: '1px solid #e5e7eb'
        }}>
          <Search size={16} color="#9ca3af" />
          <input
            type="text"
            placeholder={t('admin.users.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              marginLeft: '8px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>
        <button
          onClick={handleAdd}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            background: '#7c3aed',
            color: 'white',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          <Plus size={16} />
          {t('admin.users.newUser')}
        </button>
      </div>

      {/* User List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {filteredUsers.map(user => (
          <div
            key={user.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}
          >
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: user.isAdmin
                ? 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)'
                : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: '600',
              fontSize: '14px',
              marginRight: '12px'
            }}>
              {user.fullName?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: '500', color: '#1f2937' }}>{user.fullName}</span>
                {user.isAdmin && (
                  <span style={{
                    padding: '2px 6px',
                    background: '#f3e8ff',
                    color: '#7c3aed',
                    fontSize: '10px',
                    borderRadius: '4px',
                    fontWeight: '600'
                  }}>
                    {t('admin.users.admin')}
                  </span>
                )}
                {!user.isActive && (
                  <span style={{
                    padding: '2px 6px',
                    background: '#fee2e2',
                    color: '#dc2626',
                    fontSize: '10px',
                    borderRadius: '4px',
                    fontWeight: '600'
                  }}>
                    {t('admin.users.inactive')}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                @{user.username} · {user.departments?.length > 0
                  ? user.departments.map(d => d.name).join(', ')
                  : user.departmentName}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleEdit(user)}
                style={{
                  padding: '8px',
                  background: '#f3f4f6',
                  borderRadius: '6px'
                }}
              >
                <Edit size={16} color="#6b7280" />
              </button>
              <button
                onClick={() => handleDelete(user)}
                style={{
                  padding: '8px',
                  background: '#fef2f2',
                  borderRadius: '6px'
                }}
              >
                <Trash2 size={16} color="#dc2626" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredUsers.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          <User size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p>{t('admin.users.notFound')}</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '400px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '16px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontWeight: '600' }}>
                {editingUser ? t('admin.users.editUser') : t('admin.users.newUser')}
              </h3>
              <button onClick={() => setShowModal(false)} style={{ padding: '4px' }}>
                <X size={20} color="#6b7280" />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '16px' }}>
              {error && (
                <div style={{
                  padding: '12px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#dc2626',
                  fontSize: '13px',
                  marginBottom: '16px'
                }}>
                  {error}
                </div>
              )}

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  {t('admin.users.username')} *
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  {t('admin.users.fullName')} *
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  {t('admin.users.email')}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  {t('admin.users.password')} {!editingUser && '*'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? t('admin.users.passwordHint') : ''}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>
                  {t('admin.users.departments')} *
                </label>
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '8px',
                  maxHeight: '150px',
                  overflowY: 'auto'
                }}>
                  {departments.map(dept => (
                    <label
                      key={dept.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 8px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        background: formData.departmentIds.includes(dept.id) ? '#f3f4f6' : 'transparent'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.departmentIds.includes(dept.id)}
                        onChange={(e) => {
                          const newIds = e.target.checked
                            ? [...formData.departmentIds, dept.id]
                            : formData.departmentIds.filter(id => id !== dept.id);
                          setFormData({
                            ...formData,
                            departmentIds: newIds,
                            departmentId: newIds[0] || ''
                          });
                        }}
                        style={{ width: '16px', height: '16px' }}
                      />
                      <span style={{ fontSize: '13px' }}>{dept.name}</span>
                    </label>
                  ))}
                </div>
                {formData.departmentIds.length === 0 && (
                  <p style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>
                    {t('admin.users.departmentsRequired')}
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '13px' }}>{t('admin.users.active')}</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isAdmin}
                    onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '13px' }}>{t('admin.users.admin')}</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.canDeletePatientMessages || false}
                    onChange={(e) => setFormData({ ...formData, canDeletePatientMessages: e.target.checked })}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '13px' }}>{t('admin.users.canDeletePatientMessages')}</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#f3f4f6',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {t('admin.users.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: saving ? '#9ca3af' : '#7c3aed',
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {saving ? t('admin.users.saving') : t('admin.users.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
